import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getClients, createClient, updateClient, deactivateClient, reactivateClient } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/errors';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Contact, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

// Mirrors the currency list used elsewhere in the admin surface
// (SupplierInvoicesPage, day-receipt-form.jsx) for a consistent set.
const CURRENCIES = ['SEK', 'EUR', 'USD', 'GBP'];

// Stable backend enum values — never display strings. Localised via
// t(`clients.vat_treatment_${value}`).
const VAT_TREATMENTS = ['domestic', 'eu_reverse_charge', 'export_outside_eu'];

// Mirrors DEFAULT_VAT_RATE in backend/models/client.py — the pre-fill value
// for a new client, and the fallback if the field is ever left blank.
const DEFAULT_VAT_RATE = 25;

// Lightweight client-side check so a malformed address never makes a round
// trip just to bounce back as error.clients.email_invalid — the backend
// still validates authoritatively (this is a UX guard, not a security one).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emptyAddress() {
    return { street: '', postal_code: '', city: '', country: '' };
}

function emptyClientForm() {
    return {
        name: '',
        org_number: '',
        vat_number: '',
        notes: '',
        billing_address: emptyAddress(),
        delivery_address: emptyAddress(),
        delivery_same_as_billing: true,
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        invoice_email: '',
        their_reference: '',
        // Empty string = inherit the tenant's default_payment_terms_days —
        // mirrors the daily_base_hours convention in EmployeesPage.jsx.
        payment_terms_days: '',
        overdue_interest_pct: '',
        currency: 'SEK',
        vat_treatment: 'domestic',
        vat_rate: String(DEFAULT_VAT_RATE),
        our_reference: '',
        reference_no: '',
        fortnox_customer_number: '',
    };
}

// Small uppercase group heading used inside the create/edit dialog — mirrors
// the "Add/Edit form" label style already used in SupplierInvoicesPage's
// Manage Suppliers dialog.
function GroupHeading({ children }) {
    return (
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
            {children}
        </p>
    );
}

// Module-level (not defined inside ClientsPage) so its identity is stable
// across renders — an inline definition would be recreated on every
// keystroke (setFormData → re-render), and React would then remount these
// Inputs instead of updating them, dropping focus after every character.
function AddressFields({ address, onChange, testIdPrefix }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
                <Label htmlFor={`${testIdPrefix}-street`} className="label-uppercase mb-2 block">
                    {t('clients.address_street')}
                </Label>
                <Input
                    id={`${testIdPrefix}-street`}
                    value={address.street}
                    onChange={(e) => onChange('street', e.target.value)}
                    data-testid={`${testIdPrefix}-street`}
                />
            </div>
            <div>
                <Label htmlFor={`${testIdPrefix}-postal-code`} className="label-uppercase mb-2 block">
                    {t('clients.address_postal_code')}
                </Label>
                <Input
                    id={`${testIdPrefix}-postal-code`}
                    value={address.postal_code}
                    onChange={(e) => onChange('postal_code', e.target.value)}
                    data-testid={`${testIdPrefix}-postal-code`}
                />
            </div>
            <div>
                <Label htmlFor={`${testIdPrefix}-city`} className="label-uppercase mb-2 block">
                    {t('clients.address_city')}
                </Label>
                <Input
                    id={`${testIdPrefix}-city`}
                    value={address.city}
                    onChange={(e) => onChange('city', e.target.value)}
                    data-testid={`${testIdPrefix}-city`}
                />
            </div>
            <div className="sm:col-span-2">
                <Label htmlFor={`${testIdPrefix}-country`} className="label-uppercase mb-2 block">
                    {t('clients.address_country')}
                </Label>
                <Input
                    id={`${testIdPrefix}-country`}
                    value={address.country}
                    onChange={(e) => onChange('country', e.target.value)}
                    data-testid={`${testIdPrefix}-country`}
                />
            </div>
        </div>
    );
}

export default function ClientsPage() {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [query, setQuery] = useState('');
    const [showInactive, setShowInactive] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [formData, setFormData] = useState(emptyClientForm());
    const [saving, setSaving] = useState(false);
    const [statusUpdatingId, setStatusUpdatingId] = useState(null);

    // Debounce the search box → query, mirroring the FX-preview debounce
    // pattern in SupplierInvoicesPage (300ms, cleared on every keystroke).
    useEffect(() => {
        const timer = setTimeout(() => setQuery(searchInput.trim()), 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // requestIdRef guards against an older in-flight search response landing
    // after a newer one — same pattern as SupplierInvoicesPage.fetchAll.
    const requestIdRef = useRef(0);
    const fetchClients = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            // Always ask for inactive clients too — the inactive accordion
            // below (same pattern as ProjectsPage / EmployeesPage) splits
            // active/inactive client-side, so toggling it is instant and
            // never needs a second round trip.
            const res = await getClients({ include_inactive: true, q: query || undefined });
            if (requestId !== requestIdRef.current) return;
            setClients(res.data || []);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            setClients([]);
            toast.error(t('clients.failed_load'));
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [query, t]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    const activeClients = useMemo(() => clients.filter((c) => c.is_active), [clients]);
    const inactiveClients = useMemo(() => clients.filter((c) => !c.is_active), [clients]);

    const openCreateDialog = () => {
        setEditingClient(null);
        setFormData(emptyClientForm());
        setDialogOpen(true);
    };

    const openEditDialog = (client) => {
        setEditingClient(client);
        setFormData({
            name: client.name,
            org_number: client.org_number || '',
            vat_number: client.vat_number || '',
            notes: client.notes || '',
            billing_address: { ...emptyAddress(), ...(client.billing_address || {}) },
            delivery_address: { ...emptyAddress(), ...(client.delivery_address || {}) },
            delivery_same_as_billing: client.delivery_same_as_billing ?? true,
            contact_name: client.contact_name || '',
            contact_email: client.contact_email || '',
            contact_phone: client.contact_phone || '',
            invoice_email: client.invoice_email || '',
            their_reference: client.their_reference || '',
            payment_terms_days: client.payment_terms_days != null ? String(client.payment_terms_days) : '',
            overdue_interest_pct: client.overdue_interest_pct != null ? String(client.overdue_interest_pct) : '',
            currency: client.currency || 'SEK',
            vat_treatment: client.vat_treatment || 'domestic',
            vat_rate: client.vat_rate != null ? String(client.vat_rate) : String(DEFAULT_VAT_RATE),
            our_reference: client.our_reference || '',
            reference_no: client.reference_no || '',
            fortnox_customer_number: client.fortnox_customer_number || '',
        });
        setDialogOpen(true);
    };

    const setBillingField = (field, value) =>
        setFormData((prev) => ({ ...prev, billing_address: { ...prev.billing_address, [field]: value } }));
    const setDeliveryField = (field, value) =>
        setFormData((prev) => ({ ...prev, delivery_address: { ...prev.delivery_address, [field]: value } }));

    // Guides the user before they submit rather than only after a 400 comes
    // back — the backend enforces the same rule (error.clients.
    // vat_number_required_for_reverse_charge) as the authoritative check.
    const reverseChargeMissingVat =
        formData.vat_treatment === 'eu_reverse_charge' && !formData.vat_number.trim();

    const handleSave = async () => {
        const name = formData.name.trim();
        if (!name) {
            toast.error(t('clients.name_required'));
            return;
        }
        if (reverseChargeMissingVat) {
            toast.error(t('clients.vat_number_required_for_reverse_charge'));
            return;
        }
        if (formData.contact_email.trim() && !EMAIL_RE.test(formData.contact_email.trim())) {
            toast.error(t('clients.email_invalid'));
            return;
        }
        if (formData.invoice_email.trim() && !EMAIL_RE.test(formData.invoice_email.trim())) {
            toast.error(t('clients.email_invalid'));
            return;
        }

        // Numeric fields must never be submitted as an empty string — the
        // backend types them and will 422. A blank payment/interest field
        // means null (payment_terms_days null = "use the tenant default",
        // resolved server-side; overdue_interest_pct null is simply unset).
        // vat_rate has no such null state on the stored document, so a
        // blank/invalid entry falls back to the same default it was
        // pre-filled with rather than ever going out empty.
        const parsedPaymentTerms =
            formData.payment_terms_days === '' ? null : parseInt(formData.payment_terms_days, 10);
        const parsedOverdueInterest =
            formData.overdue_interest_pct === '' ? null : parseFloat(formData.overdue_interest_pct);
        const parsedVatRate = parseFloat(formData.vat_rate);

        setSaving(true);
        try {
            const payload = {
                name,
                org_number: formData.org_number.trim() || null,
                vat_number: formData.vat_number.trim() || null,
                notes: formData.notes,
                billing_address: formData.billing_address,
                // delivery_same_as_billing is the source of truth (PRD
                // 10.1) — the delivery address is sent exactly as the user
                // left it, never mirrored from billing here.
                delivery_address: formData.delivery_address,
                delivery_same_as_billing: formData.delivery_same_as_billing,
                contact_name: formData.contact_name.trim() || null,
                contact_email: formData.contact_email.trim() || null,
                contact_phone: formData.contact_phone.trim() || null,
                invoice_email: formData.invoice_email.trim() || null,
                their_reference: formData.their_reference.trim() || null,
                payment_terms_days: Number.isNaN(parsedPaymentTerms) ? null : parsedPaymentTerms,
                overdue_interest_pct: Number.isNaN(parsedOverdueInterest) ? null : parsedOverdueInterest,
                currency: formData.currency,
                vat_treatment: formData.vat_treatment,
                vat_rate: Number.isNaN(parsedVatRate) ? DEFAULT_VAT_RATE : parsedVatRate,
                our_reference: formData.our_reference.trim() || null,
                reference_no: formData.reference_no.trim() || null,
                fortnox_customer_number: formData.fortnox_customer_number.trim() || null,
            };

            if (editingClient) {
                await updateClient(editingClient.id, payload);
                toast.success(t('clients.client_updated'));
            } else {
                await createClient(payload);
                toast.success(t('clients.client_created'));
            }
            setDialogOpen(false);
            fetchClients();
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('clients.failed_save')));
        } finally {
            setSaving(false);
        }
    };

    // Deactivate/reactivate — never a delete affordance (Principle 2, and
    // there is deliberately no DELETE route on the backend).
    const handleToggleActive = async (client) => {
        setStatusUpdatingId(client.id);
        try {
            const res = client.is_active
                ? await deactivateClient(client.id)
                : await reactivateClient(client.id);
            setClients((prev) => prev.map((c) => (c.id === client.id ? res.data : c)));
            toast.success(
                res.data.is_active
                    ? t('clients.client_activated', { name: client.name })
                    : t('clients.client_deactivated', { name: client.name })
            );
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('clients.failed_update_status')));
        } finally {
            setStatusUpdatingId(null);
        }
    };

    const ClientRow = ({ client, muted }) => (
        <tr key={client.id} data-testid={`client-row-${client.id}`} className={muted ? 'opacity-60' : ''}>
            <td className="font-mono text-sm text-muted-foreground">{client.client_number}</td>
            <td>
                <div className="font-medium">{client.name}</div>
                <div className="text-xs text-muted-foreground">
                    {client.org_number || t('clients.no_org_number')}
                </div>
            </td>
            <td className="text-sm">
                {client.contact_name || client.contact_email ? (
                    <>
                        {client.contact_name && <div>{client.contact_name}</div>}
                        {client.contact_email && (
                            <div className="text-xs text-muted-foreground">{client.contact_email}</div>
                        )}
                    </>
                ) : (
                    <span className="text-muted-foreground">{t('clients.no_contact')}</span>
                )}
            </td>
            <td className="text-sm text-muted-foreground">{client.invoice_email || '—'}</td>
            <td className="font-mono text-sm">
                {t('clients.payment_terms_days_value', { days: client.payment_terms_days })}
            </td>
            <td>
                <Badge variant={client.is_active ? 'outline' : 'secondary'}>
                    {client.is_active ? t('common.active') : t('common.inactive')}
                </Badge>
            </td>
            <td className="text-right whitespace-nowrap">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(client)}
                    aria-label={t('common.edit')}
                    data-testid={`edit-client-${client.id}`}
                >
                    <Pencil className="w-4 h-4" />
                </Button>
                <Switch
                    checked={client.is_active}
                    disabled={statusUpdatingId === client.id}
                    onCheckedChange={() => handleToggleActive(client)}
                    aria-label={client.is_active ? t('clients.deactivate_client') : t('clients.activate_client')}
                    data-testid={`toggle-client-active-${client.id}`}
                    className="align-middle ml-1"
                />
            </td>
        </tr>
    );

    const ClientCard = ({ client }) => (
        <div key={client.id} className="border border-border p-4 space-y-2" data-testid={`client-card-${client.id}`}>
            <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{client.name}</p>
                        <span className="text-xs font-mono text-muted-foreground shrink-0">
                            #{client.client_number}
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                        {client.org_number || t('clients.no_org_number')}
                    </p>
                </div>
                <Badge variant={client.is_active ? 'outline' : 'secondary'} className="shrink-0">
                    {client.is_active ? t('common.active') : t('common.inactive')}
                </Badge>
            </div>
            {(client.contact_name || client.contact_email) && (
                <p className="text-xs text-muted-foreground">
                    {[client.contact_name, client.contact_email].filter(Boolean).join(' · ')}
                </p>
            )}
            <p className="text-xs text-muted-foreground font-mono">
                {t('clients.payment_terms_days_value', { days: client.payment_terms_days })}
            </p>
            <div className="flex items-center justify-between pt-2 border-t border-border">
                <Switch
                    checked={client.is_active}
                    disabled={statusUpdatingId === client.id}
                    onCheckedChange={() => handleToggleActive(client)}
                    aria-label={client.is_active ? t('clients.deactivate_client') : t('clients.activate_client')}
                    data-testid={`toggle-client-active-mobile-${client.id}`}
                />
                <Button
                    variant="ghost"
                    size="sm"
                    className="min-h-[44px] min-w-[44px] p-0"
                    onClick={() => openEditDialog(client)}
                    aria-label={t('common.edit')}
                >
                    <Pencil className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );

    if (loading && clients.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="clients-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('clients.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('clients.subtitle')}</p>
                </div>
                <Button
                    className="uppercase tracking-wider"
                    onClick={openCreateDialog}
                    data-testid="add-client-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('clients.add_client')}
                </Button>
            </div>

            {/* Search */}
            <div className="flex items-center gap-2 max-w-sm">
                <Label htmlFor="clients-search-input" className="label-uppercase text-xs text-muted-foreground shrink-0">
                    {t('common.search')}
                </Label>
                <Input
                    id="clients-search-input"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t('clients.search_placeholder')}
                    data-testid="clients-search-input"
                />
            </div>

            {/* Active Clients List */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    {activeClients.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <Contact className="w-12 h-12 mx-auto mb-4 opacity-30" />
                            <p>{query ? t('clients.no_clients_search') : t('clients.no_clients')}</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('clients.col_number')}</th>
                                            <th>{t('clients.col_name')}</th>
                                            <th>{t('clients.col_contact')}</th>
                                            <th>{t('clients.col_invoice_email')}</th>
                                            <th>{t('clients.col_payment_terms')}</th>
                                            <th>{t('clients.col_status')}</th>
                                            <th className="text-right">{t('clients.col_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeClients.map((client) => (
                                            <ClientRow key={client.id} client={client} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden p-3 space-y-3">
                                {activeClients.map((client) => (
                                    <ClientCard key={client.id} client={client} />
                                ))}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Inactive Clients Accordion — same pattern as ProjectsPage / EmployeesPage */}
            {inactiveClients.length > 0 && (
                <Card className="border border-border shadow-none">
                    <button
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                        onClick={() => setShowInactive((prev) => !prev)}
                        aria-expanded={showInactive}
                    >
                        <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                            {t('clients.inactive_clients', { count: inactiveClients.length })}
                        </span>
                        {showInactive
                            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        }
                    </button>
                    {showInactive && (
                        <CardContent className="p-0 border-t border-border">
                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('clients.col_number')}</th>
                                            <th>{t('clients.col_name')}</th>
                                            <th>{t('clients.col_contact')}</th>
                                            <th>{t('clients.col_invoice_email')}</th>
                                            <th>{t('clients.col_payment_terms')}</th>
                                            <th>{t('clients.col_status')}</th>
                                            <th className="text-right">{t('clients.col_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inactiveClients.map((client) => (
                                            <ClientRow key={client.id} client={client} muted />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Mobile cards */}
                            <div className="md:hidden p-3 space-y-3 opacity-60">
                                {inactiveClients.map((client) => (
                                    <ClientCard key={client.id} client={client} />
                                ))}
                            </div>
                        </CardContent>
                    )}
                </Card>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editingClient ? t('clients.edit_client') : t('clients.new_client')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {/* Identity */}
                        <div className="space-y-3">
                            <GroupHeading>{t('clients.group_identity')}</GroupHeading>
                            {editingClient && (
                                <div>
                                    <Label htmlFor="client-client-number" className="label-uppercase mb-2 block">
                                        {t('clients.label_client_number')}
                                    </Label>
                                    <Input
                                        id="client-client-number"
                                        value={editingClient.client_number}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            )}
                            <div>
                                <Label htmlFor="client-name-input" className="label-uppercase mb-2 block">
                                    {t('clients.label_name')}
                                </Label>
                                <Input
                                    id="client-name-input"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder={t('clients.placeholder_name')}
                                    data-testid="client-name-input"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="client-org-number" className="label-uppercase mb-2 block">
                                        {t('clients.label_org_number')}
                                    </Label>
                                    <Input
                                        id="client-org-number"
                                        value={formData.org_number}
                                        onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                                        placeholder={t('clients.org_number_placeholder')}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="client-vat-number-input" className="label-uppercase mb-2 block">
                                        {t('clients.label_vat_number')}
                                    </Label>
                                    <Input
                                        id="client-vat-number-input"
                                        value={formData.vat_number}
                                        onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                                        placeholder={t('clients.vat_number_placeholder')}
                                        className={reverseChargeMissingVat ? 'border-destructive' : ''}
                                        data-testid="client-vat-number-input"
                                    />
                                    {reverseChargeMissingVat && (
                                        <p className="text-xs text-destructive mt-1">
                                            {t('clients.vat_number_required_for_reverse_charge')}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="client-notes" className="label-uppercase mb-2 block">
                                    {t('clients.label_notes')}
                                </Label>
                                <Textarea
                                    id="client-notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={2}
                                />
                            </div>
                        </div>

                        {/* Addresses */}
                        <div className="border-t border-border pt-4 space-y-3">
                            <GroupHeading>{t('clients.group_addresses')}</GroupHeading>
                            <div>
                                {/* A plain heading, not a form <Label> — this text ("Billing
                                    Address") titles a group of four fields, not one, so it
                                    cannot correctly carry a single htmlFor target. Each field
                                    inside AddressFields has its own properly associated Label. */}
                                <p className="label-uppercase mb-2 block">
                                    {t('clients.label_billing_address')}
                                </p>
                                <AddressFields
                                    address={formData.billing_address}
                                    onChange={setBillingField}
                                    testIdPrefix="client-billing"
                                />
                            </div>
                            <div className="flex items-center justify-between pt-2">
                                <Label htmlFor="client-delivery-same-switch" className="label-uppercase">
                                    {t('clients.label_delivery_same_as_billing')}
                                </Label>
                                <Switch
                                    id="client-delivery-same-switch"
                                    checked={formData.delivery_same_as_billing}
                                    onCheckedChange={(checked) =>
                                        setFormData({ ...formData, delivery_same_as_billing: checked })
                                    }
                                    data-testid="client-delivery-same-switch"
                                />
                            </div>
                            {!formData.delivery_same_as_billing && (
                                <div>
                                    {/* Group heading, not a per-field Label — see the matching
                                        comment above the billing address heading. */}
                                    <p className="label-uppercase mb-2 block">
                                        {t('clients.label_delivery_address')}
                                    </p>
                                    <AddressFields
                                        address={formData.delivery_address}
                                        onChange={setDeliveryField}
                                        testIdPrefix="client-delivery"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Contacts */}
                        <div className="border-t border-border pt-4 space-y-3">
                            <GroupHeading>{t('clients.group_contacts')}</GroupHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="client-contact-name" className="label-uppercase mb-2 block">
                                        {t('clients.label_contact_name')}
                                    </Label>
                                    <Input
                                        id="client-contact-name"
                                        value={formData.contact_name}
                                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="client-contact-phone" className="label-uppercase mb-2 block">
                                        {t('clients.label_contact_phone')}
                                    </Label>
                                    <Input
                                        id="client-contact-phone"
                                        value={formData.contact_phone}
                                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="client-contact-email" className="label-uppercase mb-2 block">
                                    {t('clients.label_contact_email')}
                                </Label>
                                <Input
                                    id="client-contact-email"
                                    type="email"
                                    value={formData.contact_email}
                                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                                />
                                <p className="text-xs text-muted-foreground mt-1">{t('clients.contact_email_hint')}</p>
                            </div>
                            <div>
                                <Label htmlFor="client-invoice-email-input" className="label-uppercase mb-2 block">
                                    {t('clients.label_invoice_email')}
                                </Label>
                                <Input
                                    id="client-invoice-email-input"
                                    type="email"
                                    value={formData.invoice_email}
                                    onChange={(e) => setFormData({ ...formData, invoice_email: e.target.value })}
                                    data-testid="client-invoice-email-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">{t('clients.invoice_email_hint')}</p>
                            </div>
                            <div>
                                <Label htmlFor="client-their-reference" className="label-uppercase mb-2 block">
                                    {t('clients.label_their_reference')}
                                </Label>
                                <Input
                                    id="client-their-reference"
                                    value={formData.their_reference}
                                    onChange={(e) => setFormData({ ...formData, their_reference: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Invoicing terms */}
                        <div className="border-t border-border pt-4 space-y-3">
                            <GroupHeading>{t('clients.group_invoicing_terms')}</GroupHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="client-payment-terms-days" className="label-uppercase mb-2 block">
                                        {t('clients.label_payment_terms_days')}
                                    </Label>
                                    <Input
                                        id="client-payment-terms-days"
                                        type="number"
                                        min="0"
                                        max="365"
                                        value={formData.payment_terms_days}
                                        onChange={(e) =>
                                            setFormData({ ...formData, payment_terms_days: e.target.value })
                                        }
                                        placeholder={t('clients.payment_terms_days_placeholder')}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="client-overdue-interest-pct" className="label-uppercase mb-2 block">
                                        {t('clients.label_overdue_interest_pct')}
                                    </Label>
                                    <Input
                                        id="client-overdue-interest-pct"
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={formData.overdue_interest_pct}
                                        onChange={(e) =>
                                            setFormData({ ...formData, overdue_interest_pct: e.target.value })
                                        }
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="client-currency-select" className="label-uppercase mb-2 block">
                                        {t('clients.label_currency')}
                                    </Label>
                                    <Select
                                        value={formData.currency}
                                        onValueChange={(v) => setFormData({ ...formData, currency: v })}
                                    >
                                        <SelectTrigger id="client-currency-select" data-testid="client-currency-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CURRENCIES.map((c) => (
                                                <SelectItem key={c} value={c}>{c}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="client-vat-rate" className="label-uppercase mb-2 block">
                                        {t('clients.label_vat_rate')}
                                    </Label>
                                    <Input
                                        id="client-vat-rate"
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={formData.vat_rate}
                                        onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="client-vat-treatment-select" className="label-uppercase mb-2 block">
                                    {t('clients.label_vat_treatment')}
                                </Label>
                                <Select
                                    value={formData.vat_treatment}
                                    onValueChange={(v) => setFormData({ ...formData, vat_treatment: v })}
                                >
                                    <SelectTrigger id="client-vat-treatment-select" data-testid="client-vat-treatment-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {VAT_TREATMENTS.map((v) => (
                                            <SelectItem key={v} value={v}>
                                                {t(`clients.vat_treatment_${v}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {formData.vat_treatment === 'eu_reverse_charge' && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('clients.vat_treatment_reverse_charge_hint')}
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="client-our-reference" className="label-uppercase mb-2 block">
                                        {t('clients.label_our_reference')}
                                    </Label>
                                    <Input
                                        id="client-our-reference"
                                        value={formData.our_reference}
                                        onChange={(e) => setFormData({ ...formData, our_reference: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="client-reference-no" className="label-uppercase mb-2 block">
                                        {t('clients.label_reference_no')}
                                    </Label>
                                    <Input
                                        id="client-reference-no"
                                        value={formData.reference_no}
                                        onChange={(e) => setFormData({ ...formData, reference_no: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Integration */}
                        <div className="border-t border-border pt-4 space-y-3">
                            <GroupHeading>{t('clients.group_integration')}</GroupHeading>
                            <div>
                                <Label htmlFor="client-fortnox-customer-number" className="label-uppercase mb-2 block">
                                    {t('clients.label_fortnox_customer_number')}
                                </Label>
                                <Input
                                    id="client-fortnox-customer-number"
                                    value={formData.fortnox_customer_number}
                                    onChange={(e) =>
                                        setFormData({ ...formData, fortnox_customer_number: e.target.value })
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-client-btn">
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('common.saving')}
                                </>
                            ) : (
                                t('common.save')
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
